import { planningPeriodInterface } from 'Interfaces/FDPInterfaces/tmsInterfaces';

export const getOneSmallerDate = (date: Date) => {
  date.setDate(date.getDate() - 1);
  return date;
};

export const getOneBiggerDate = (date: Date) => {
  date.setDate(date.getDate() + 1);
  return date;
};

export const getTomorowsDate = (date: Date = new Date()) => {
  // Clone the date to avoid mutating the passed reference or the default new Date() if used elsewhere (though new Date() is fresh)
  const dateClone = new Date(date);
  return getOneBiggerDate(dateClone);
};

export function formatDate(date_string: string) {
  // Parse YYYY-MM-DD as local date so the calendar date is preserved (no timezone shift)
  let date: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(date_string)) {
    const [y, m, d] = date_string.split('-').map(Number);
    date = new Date(y, m - 1, d);
  } else {
    date = new Date(date_string);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDateToDateTimeString(date_string: string) {
  const date = new Date(date_string);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export function getFirstDayOfMonth(dateString: string) {
  // Parse the input date string into a Date object
  const date = new Date(dateString);

  // Get the year and month from the date object
  const year = date.getFullYear();
  const month = date.getMonth();

  // Create a new Date object set to the first day of the month
  const firstDayOfMonth = new Date(year, month, 1);

  return firstDayOfMonth;
}

export function getFirstDayFromDateString(dateString: string) {
  const arr = dateString.split('-');
  // Get the current month and year
  const currentMonth = parseInt(arr[1]) - 1;
  const currentYear = parseInt(arr[0]);

  // Create a new Date object for the first day of the current month
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  return firstDayOfMonth;
}

export function getLastDayOfMonth(dateString: string) {
  // Parse the input date string into a Date object
  const date = new Date(dateString);

  // Get the year and month from the date object
  const year = date.getFullYear();
  const month = date.getMonth();

  // Create a new Date object set to the first day of the next month
  // passing 0 as the day will give the last day of the previous month
  const lastDayOfMonth = new Date(year, month + 1, 0);

  return lastDayOfMonth;
}

export function getNextMonthFirstDay(dateString: string) {
  // Parse the input date string into a Date object
  const date = new Date(dateString);

  // Get the current month and year
  const currentMonth = date.getMonth();
  const currentYear = date.getFullYear();

  // Calculate the next month and year
  let nextMonth = currentMonth + 1;
  let nextYear = currentYear;
  if (nextMonth > 11) {
    nextMonth = 0;
    nextYear += 1;
  }

  // Create a new Date object for the first day of the next month
  const nextMonthFirstDay = new Date(nextYear, nextMonth, 1);

  return nextMonthFirstDay;
}

export function convertIsoToDateString(isoString: string): string | null {
  try {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const day = String(date.getDate()).padStart(2, '0');
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const twelveHourFormat = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours; //Handle 12 AM/PM

    return `${year}/${month}/${day} ${twelveHourFormat}:${minutes} ${ampm}`;
  } catch (error) {
    console.error('Error converting ISO string:', error);
    return null; // Or throw the error, depending on your error handling preference.
  }
}

export const convertPlanningPeriodArray = (dates: string[]): { label: string; code: string }[] => {
  const result = dates.map(date => {
    const [year, month, day] = date.split('-');
    const dateObj = new Date(Number(year), Number(month) - 1, Number(day));

    // Calculate startMonth and startYear
    // Note: The original logic incremented month by 1 immediately.
    // Assuming the date format is YYYY-MM-DD and we want to show a range starting next month?
    // Or was it parsing 1-based month? date.split results in strings.
    // new Date(year, month-1, day) is correct for YYYY-MM-DD.
    // Original code: dateObj.setMonth(dateObj.getMonth() + 1)

    const startMonthDate = new Date(dateObj);
    startMonthDate.setMonth(startMonthDate.getMonth() + 1);
    const startMonth = startMonthDate.toLocaleString('default', { month: 'short' });
    const startYear = startMonthDate.getFullYear();

    const endDate = new Date(startMonthDate);
    endDate.setMonth(endDate.getMonth() + 11);
    const endYear = endDate.getFullYear();
    const endMonth = endDate.toLocaleString('default', { month: 'short' });

    return {
      label: `${startMonth} ${startYear} - ${endMonth} ${endYear}`,
      code: date,
    };
  });
  return result;
};

export const convertSalesPlanPlanningPeriodLabelArray = (
  dates: string[]
): { label: string; code: string }[] => {
  const result = dates.map(date => {
    const [year, month, day] = date.split('-');
    const dateObj = new Date(Number(year), Number(month) - 1, Number(day));

    const startMonthDate = new Date(dateObj);
    startMonthDate.setMonth(startMonthDate.getMonth() + 1);
    const startMonth = startMonthDate.toLocaleString('default', { month: 'short' });
    const startYear = startMonthDate.getFullYear();

    const endDate = new Date(startMonthDate);
    endDate.setMonth(endDate.getMonth() + 14);
    const endYear = endDate.getFullYear();
    const endMonth = endDate.toLocaleString('default', { month: 'short' });

    return {
      label: `${startMonth} ${startYear} - ${endMonth} ${endYear}`,
      code: date,
    };
  });
  return result;
};

export const convertSalesPlanPlanningPeriodArray = (
  dates: planningPeriodInterface[]
): { label: string; code: string }[] => {
  const result = dates.map(date => {
    const [year, month, day] = date.PlanningPeriod.split('-');
    const dateObj = new Date(Number(year), Number(month) - 1, Number(day));

    const startMonthDate = new Date(dateObj);
    startMonthDate.setMonth(startMonthDate.getMonth() + 1);
    const startMonth = startMonthDate.toLocaleString('default', { month: 'short' });
    const startYear = startMonthDate.getFullYear();

    const endDate = new Date(startMonthDate);
    endDate.setMonth(endDate.getMonth() + 14);
    const endYear = endDate.getFullYear();
    const endMonth = endDate.toLocaleString('default', { month: 'short' });

    return {
      label: `${startMonth} ${startYear} - ${endMonth} ${endYear}`,
      code: date.PlanningPeriod,
    };
  });
  return result;
};

export const getCurrentMonthDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = '01'; // First day of the month
  return `${year}-${month}-${day}`;
};

export class DateConversionUtility {
  static convertDBDateToLocal(dateInput: string | Date | null | undefined): Date | null {
    if (!dateInput) return null;

    if (dateInput instanceof Date) {
      // Clone date to avoid mutation issues if passed by reference elsewhere
      return new Date(dateInput);
    }

    const str = String(dateInput);
    // Handle YYYY-MM-DD format (parse as local midnight)
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [year, month, day] = str.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    // Handle MM-DD-YYYY format (parse as local midnight)
    if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
      const [month, day, year] = str.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    // Handle YYYY-MM-DD HH:mm:ss...
    const date = new Date(str);
    if (!isNaN(date.getTime())) {
      return date;
    }

    return null;
  }

  static convertLocalToDBDateString(date: Date | null | undefined): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
